import { defineStore } from 'pinia'
import type { Router } from 'vue-router'

export type AppDomain = 'write' | 'community' | 'read'

/** Wiped on logout by `clearUserLocalStorage` (allowlist does not keep this key). */
export const APP_DOMAIN_STORAGE_KEY = 'appDomain'

export const APP_DOMAIN_HOME: Record<Exclude<AppDomain, 'read'>, string> = {
  write: '/projects',
  community: '/community/activity',
}

function parseDomain(value: unknown): AppDomain | null {
  if (value === 'write' || value === 'community' || value === 'read') {
    return value
  }
  return null
}

function readStoredDomain(): AppDomain {
  if (typeof localStorage === 'undefined') {
    return 'write'
  }
  return parseDomain(localStorage.getItem(APP_DOMAIN_STORAGE_KEY)) ?? 'write'
}

function writeStoredDomain(domain: AppDomain) {
  if (typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(APP_DOMAIN_STORAGE_KEY, domain)
  } catch {
    // Ignore quota or private-mode failures.
  }
}

export const useAppDomainStore = defineStore('appDomain', {
  state: () => ({
    selected: readStoredDomain(),
  }),

  actions: {
    persist(domain: AppDomain) {
      this.selected = domain
      writeStoredDomain(domain)
    },

    syncFromRoute(input: { routeDomain: unknown; isGuest: boolean }) {
      if (input.isGuest) {
        this.selected = 'write'
        return
      }
      const fromRoute = parseDomain(input.routeDomain)
      // Keep the last Escribir/Comunidad choice on reserved 'read' routes.
      if (fromRoute === 'write' || fromRoute === 'community') {
        this.persist(fromRoute)
      }
    },

    selectDomain(domain: Exclude<AppDomain, 'read'>, router: Router) {
      this.persist(domain)
      const home = APP_DOMAIN_HOME[domain]
      if (home) {
        void router.push(home)
      }
    },

    toggleWriteCommunity(router: Router) {
      const next = this.selected === 'write' ? 'community' : 'write'
      this.selectDomain(next, router)
    },
  },
})
